import { Avatar, Box, Typography, useTheme } from '@mui/material'
import { forwardRef, useCallback, useImperativeHandle, useMemo, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Security } from '../../../../proto/generated/security'
import './oiChart.css'
import { setSpanTextContent } from '../../../multiStikeChart/multiStrikeChartMetadataDom'
import { selectMultiStrikeStatusLineSettings, selectOiWidgetSettings } from '../../../../selectors/chart'
import { LOGOS_BASE_URL } from '../../../../assets/constants'
import { getColorForSymbol } from '../../../../util/general'
import SeriesTitleButton from '../../../chartV2/seriesMetaDisplay/SeriesTitleButton'
import OptionsExpiryMenuBody from '../../../../pages/home/optionsChartHeader/optionsChartExpiryMenu/optionsExpiryMenuBody'
import { DropDownExpiries } from '../../../../reducer/options.reducer'
import { getSecurityKey } from '../../../../util/security'
import { setSecuritySearchOpen } from '../../../../reducer/home.reducer'
import tinycolor from 'tinycolor2'
import {
    getMultiStrikeMetadataTableRowSx,
    MULTI_STRIKE_METADATA_LABEL_CELL_SX,
    MULTI_STRIKE_METADATA_STRIKE_CELL_SX,
    MULTI_STRIKE_METADATA_TABLE_SX,
    MULTI_STRIKE_METADATA_VALUE_CELL_SX,
} from '../../../multiStikeChart/chartWrapper/chartMetadata/multiStrikeMetadataLayout'

interface Props {
    viewKey: string
    security: Security
    selectedExpiry: {
        expirySeconds: string
        expiry: string
    } | undefined
    setSelectedExpiry: React.Dispatch<React.SetStateAction<{
        expirySeconds: string
        expiry: string
    } | undefined>>
    onSecuritySelected?: (key: string, expressionMode: boolean, securities: string[]) => void
}

export type OiChartMetaDataHandle = {
    updateCrosshair: (strike: number, callOi: string, putOi: string) => void
}

const OiChartMetaData = forwardRef<OiChartMetaDataHandle, Props>((props, ref) => {
    const theme = useTheme()
    const dispatch = useDispatch()
    const { viewKey, security, selectedExpiry, setSelectedExpiry, onSecuritySelected } = props
    const statusLineSettings = useSelector(selectMultiStrikeStatusLineSettings(viewKey))
    const oiWidgetSettings = useSelector(selectOiWidgetSettings(viewKey))
    const strikeRef = useRef<HTMLSpanElement>(null)
    const callOiRef = useRef<HTMLSpanElement>(null)
    const putOiRef = useRef<HTMLSpanElement>(null)

    const showLogo = statusLineSettings.logo ?? true
    const showSymbolName = statusLineSettings.showSymbolName ?? true
    const showStrikeMetadata =
        (statusLineSettings.indicatorTitle ?? true) && (statusLineSettings.indicatorValues ?? true)
    const showCall = oiWidgetSettings.showCall ?? true
    const showPut = oiWidgetSettings.showPut ?? true
    const callDisplayColor = tinycolor(oiWidgetSettings.callColor).toRgbString()
    const putDisplayColor = tinycolor(oiWidgetSettings.putColor).toRgbString()

    const metaDataStyles = useMemo(() => {
        return {
            fontSize: '14px',
            width: 'fit-content',
            height: '24px',
            pl: '4px',
            pr: '4px',
            boxSizing: 'border-box' as const,
        }
    }, [])

    useImperativeHandle(ref, () => ({
        updateCrosshair(strike: number, callOi: string, putOi: string) {
            setSpanTextContent(strikeRef.current, String(strike))
            setSpanTextContent(callOiRef.current, callOi)
            setSpanTextContent(putOiRef.current, putOi)
        },
    }), [])

    const logoNode = useMemo(() => {
        if (!showLogo || !security) return null
        if (security.logoUrl) {
            return (
                <Avatar
                    alt={security.displayName ?? security.symbol}
                    src={`${LOGOS_BASE_URL}${security.logoUrl}.svg`}
                    sx={{ width: 18, height: 18, flexShrink: 0 }}
                />
            )
        }
        return (
            <Avatar
                alt={security.displayName ?? security.symbol}
                sx={{
                    width: 18,
                    height: 18,
                    flexShrink: 0,
                    fontSize: 10,
                    color: 'white',
                    bgcolor: getColorForSymbol(security.symbol || security.code || '', theme.palette.background.default),
                }}
            >
                {(security.symbol || security.code || '')
                    .split(' ')
                    .map((word) => word[0]?.toUpperCase())
                    .slice(0, 2)
                    .join('')}
            </Avatar>
        )
    }, [security, showLogo, theme.palette.background.default])

    const titleText = security?.displayName ?? ''

    const underlyingToken = useMemo(
        () => getSecurityKey(security?.exchange ?? 0, security?.code ?? ''),
        [security?.exchange, security?.code]
    )

    const onTitleClick = useCallback(() => {
        if (!security) return
        const secKey = getSecurityKey(security.exchange, security.code)
        dispatch(
            setSecuritySearchOpen({
                open: true,
                key: secKey,
                isExpression: false,
                onSecuritySelected,
            })
        )
    }, [dispatch, onSecuritySelected, security])

    const handleExpirySelect = useCallback(
        (dropdownExpiry: DropDownExpiries) => {
            setSelectedExpiry({
                expiry: dropdownExpiry.expiry.name,
                expirySeconds: String(dropdownExpiry.expiry.expiry),
            })
        },
        [setSelectedExpiry]
    )

    return (
        <Box className="pane-meta-container font-feature-num" sx={{ pointerEvents: 'all' }}>
            <Typography className="oi-series-name" sx={{ ...metaDataStyles, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                {(showLogo || showSymbolName) ? (
                    <SeriesTitleButton onClick={onTitleClick}>
                        {showLogo ? logoNode : null}
                        {showSymbolName ? (
                            <Box
                                component="span"
                                sx={{
                                    fontWeight: 500,
                                    minWidth: 0,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                {titleText}
                            </Box>
                        ) : null}
                    </SeriesTitleButton>
                ) : null}
                {selectedExpiry?.expiry ? (
                    <OptionsExpiryMenuBody
                        underlyingToken={underlyingToken}
                        expiryLabel={selectedExpiry.expiry}
                        onSelect={handleExpirySelect}
                        hidePastExpiry
                        hideDerivedInMenu
                        excludeDerivedDropdown
                    />
                ) : null}
            </Typography>
            {showStrikeMetadata ? (
                <Box component="table" sx={MULTI_STRIKE_METADATA_TABLE_SX} className="oi-series-name font-feature-num">
                    <Box component="tbody">
                        <Box component="tr" sx={getMultiStrikeMetadataTableRowSx(theme)}>
                            <Box component="td" sx={MULTI_STRIKE_METADATA_STRIKE_CELL_SX}>
                                <span ref={strikeRef} />
                            </Box>
                            <Box component="td" sx={{ ...MULTI_STRIKE_METADATA_LABEL_CELL_SX, cursor: 'default' }}>
                                CE
                            </Box>
                            <Box
                                component="td"
                                sx={{
                                    ...MULTI_STRIKE_METADATA_VALUE_CELL_SX,
                                    color: callDisplayColor,
                                    fontWeight: 500,
                                    opacity: showCall ? 1 : 0.3,
                                }}
                            >
                                <span ref={callOiRef} />
                            </Box>
                            <Box component="td" sx={{ ...MULTI_STRIKE_METADATA_LABEL_CELL_SX, cursor: 'default' }}>
                                PE
                            </Box>
                            <Box
                                component="td"
                                sx={{
                                    ...MULTI_STRIKE_METADATA_VALUE_CELL_SX,
                                    color: putDisplayColor,
                                    fontWeight: 500,
                                    opacity: showPut ? 1 : 0.3,
                                }}
                            >
                                <span ref={putOiRef} />
                            </Box>
                        </Box>
                    </Box>
                </Box>
            ) : null}
        </Box>
    )
})

OiChartMetaData.displayName = 'OiChartMetaData'

export default OiChartMetaData
